import { useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { authApi } from '../../api/endpoints/auth';
import { handleApiError } from '../../api/errorMapping';
import { useLoginFlowStore } from '../../stores/loginFlowStore';
import { LoadingSpinner } from '../shared/LoadingSpinner';

interface Props {
    email: string;
    tempPassword: string;
}

export function ActivateAccount({ email, tempPassword }: Props) {
    const { t } = useTranslation(['auth']);
    const navigate = useNavigate();
    const firedRef = useRef(false);
    const setStoreEmail = useLoginFlowStore((s) => s.setEmail);
    const setChallenge = useLoginFlowStore((s) => s.setChallenge);

    const mutation = useMutation({
        mutationFn: () => authApi.login({ email, password: tempPassword }),
        // El challenge viaja en `resp.challenge`, NO en `resp.status`/`resp.challengeType`: ese es
        // el shape de `LoginResponseLocal` (su propio comentario cierra TD-WEBAPP-003) y es el que
        // lee LoginForm. Con los campos viejos la condición era `undefined === 'CHALLENGE'`,
        // siempre falsa, así que el link de activación caía en `/` en vez de llevar a "define tu
        // contraseña".
        //
        // Y el session/email viajan por el STORE, no por la URL (DEC-RBAC-055) — mismo camino que
        // LoginForm. Por el search param, `NewPasswordForm` recibía `email: ''` (nunca se mandaba)
        // y el challenge salía sin identidad; peor aún, un `session` viejo en sessionStorage le
        // ganaba al fresco, porque el store tiene prioridad sobre la URL.
        onSuccess: async (resp) => {
            if (resp.challenge?.challengeType === 'NEW_PASSWORD_REQUIRED') {
                setStoreEmail(email);
                setChallenge({
                    session: resp.challenge.session,
                    challengeType: 'NEW_PASSWORD_REQUIRED',
                });
                await navigate({ to: '/login/new-password' });
            } else {
                await navigate({ to: '/' });
            }
        },
        onError: (err) => {
            handleApiError(err);
            void navigate({ to: '/login' });
        },
    });

    useEffect(() => {
        if (firedRef.current) return;
        firedRef.current = true;
        mutation.mutate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return <LoadingSpinner label={t('auth:activating')} size="lg" />;
}
